import { InfoIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckoutType } from "../../app/types/checkoutType";
import { decodeHtmlCharCodes } from "../../utils/stringCodeFormatter";
import { useAppConfig } from "../../hooks/useAppConfig";

type CheckoutSuccessSectionProps = {
  checkout: CheckoutType;
};

export const CheckoutSuccessSection = ({ checkout }: CheckoutSuccessSectionProps) => {
  const navigate = useNavigate();
  const { ctaColor, ctaTextColor } = useAppConfig();

  const handleContinueShopping = () => {
    navigate(-2);
  };

  const orderNumber = checkout.receipt?.result || 'N/A';
  const isSuccess = checkout.receipt?.status === "Success";

  return (
    <Box p={"1em"} color={process.env.REACT_APP_PARTNER_PRIMARY}>
      {/* Header con successo */}
      <Heading
        as="h1"
        lineHeight={1.1}
        fontSize={{ base: "2xl", sm: "3xl" }}
        textAlign="center"
        mb="1em"
        color={isSuccess ? "green.500" : "red.500"}
      >
        {isSuccess
          ? `✅ Conferma ordine n. ${orderNumber}`
          : "❌ Si è verificato un errore. L'ordine non è stato processato"
        }
      </Heading>

      {/* Dettagli indirizzo e contatto */}
      <SimpleGrid columns={[1, 2, 2]} mb="2em" spacing={4}>
        <Box
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          bg="gray.50"
        >
          <Heading pb={3} as="h6" fontSize={"1em"} fontWeight="semibold" color="gray.700">
            📍 Indirizzo di spedizione:
          </Heading>
          <Text>{checkout.indirizzo}</Text>
          <Text>
            {`${checkout.cap} - ${checkout.citta} - ${checkout.provincia}`}
          </Text>
          <Text mt={2}>
            <Text as="span" fontWeight="semibold">Note:</Text> {checkout.note || 'Nessuna nota specificata'}
          </Text>
        </Box>

        <Box
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          bg="gray.50"
        >
          <Heading pb={3} as="h6" fontSize={"1em"} fontWeight="semibold" color="gray.700">
            📞 Dati di contatto:
          </Heading>
          <Text>
            <Text as="span" fontWeight="semibold">Telefono:</Text> {checkout.telefono}
          </Text>
          <Text>
            <Text as="span" fontWeight="semibold">Email:</Text> {checkout.email}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Alert informativo */}
      <Alert
        status="info"
        bgColor={process.env.REACT_APP_PARTNER_SECONDARY}
        borderRadius="md"
        mb="2em"
      >
        <InfoIcon color={process.env.REACT_APP_PARTNER_PRIMARY} m="0.4em" fontSize="2xl" />
        <Box flex="1">
          <AlertTitle color={process.env.REACT_APP_PARTNER_PRIMARY}>
            🎉 Conserva il tuo numero di ordine.
          </AlertTitle>
          <AlertDescription color={process.env.REACT_APP_PARTNER_PRIMARY} display="block">
            Per assistenza chiamaci al numero{" "}
            <Link fontWeight="bold" href="tel:0236638510" color={ctaColor}>
              02 3663 8510
            </Link>{" "}
            o scrivici a{" "}
            <Link fontWeight="bold" href="mailto:ordini@wishlist.it" color={ctaColor}>
              ordini@wishlist.it
            </Link>
            .
          </AlertDescription>
        </Box>
      </Alert>

      {/* Call to action */}
      <VStack p={"2em"} color={process.env.REACT_APP_PARTNER_PRIMARY} spacing={4}>
        <Button
          onClick={handleContinueShopping}
          bgColor={ctaColor}
          color={ctaTextColor}
          size="lg"
          _hover={{
            opacity: 0.8,
            transform: "translateY(-1px)"
          }}
          _active={{
            transform: "scale(0.98)",
          }}
        >
          🛍️ Continua lo shopping
        </Button>
        <Text textAlign="center" fontSize="md">
          Con il tuo credito puoi richiedere un altro buono o prodotto
        </Text>
      </VStack>

      {/* Dettagli prodotto ordinato */}
      {checkout.prodotto && (
        <Box>
          <Heading as="h3" fontSize="lg" mb={3} textAlign="center">
            📦 Prodotto ordinato:
          </Heading>
          <Stack
            w={"100%"}
            border={"1px solid #d3d3d394"}
            borderRadius="md"
            direction={["column", "column", "row"]}
            bg="white"
            boxShadow="sm"
          >
            <Center p="1em">
              <Image
                boxSize={["120px", "150px", "180px"]}
                src={`${checkout.prodotto?.baseURL}${checkout.prodotto?.imagename}`}
                alt={decodeHtmlCharCodes(checkout.prodotto?.descrizione)}
                borderRadius="md"
                objectFit="cover"
              />
              <Box w={["xs", "md"]} p={4}>
                <Heading fontSize={["sm", "md", "lg"]} mb="1em" color={ctaColor}>
                  {decodeHtmlCharCodes(checkout.prodotto?.productname)}
                </Heading>
                <Text fontSize={["xs", "sm", "md"]} color="gray.600" lineHeight="1.5">
                  {decodeHtmlCharCodes(checkout.prodotto?.descrizione)}
                </Text>
              </Box>
            </Center>
          </Stack>
        </Box>
      )}


    </Box>
  );
}; 